import React, { useEffect, useMemo, useState } from 'react';
import { BrioModal, BrioModalBody, BrioButton } from '../../components/BrioComponents';
import { canonicalUnit } from '../utils/measureUnits';

// ============================================================================
// Combine some of an item's versions into one.
//
// The curator ticks the versions to merge and picks, among them, the one whose
// wording and unit stay. Every rate on the other ticked versions moves onto it
// and those versions are deleted (boq.combine_library_versions); one still used
// by a project BOQ, an FF&E schedule or an issued bill is archived instead, so
// that document keeps its spec. Unticked versions are left alone.
//
// Refused here when the ticked versions are priced in different units: a rate
// per Rmt moved onto a per m2 spec would read as the wrong figure, and the cure
// for that is Split. Only live versions are listed (boq.split_candidates).
// ============================================================================

const cell = { padding: '0.35rem 0.5rem', borderBottom: '1px solid var(--brio-border-subtle, #eee)', verticalAlign: 'top' };
const muted = { color: 'var(--brio-text-muted)', fontSize: '0.76rem' };
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function CombineVersionsModal({ itemCode, loadCandidates, onCombine, onClose }) {
  const [versions, setVersions] = useState(null);
  const [ticked, setTicked] = useState(() => new Set());
  const [keepId, setKeepId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    loadCandidates(itemCode).then((res) => {
      if (!live) return;
      if (!res.success) { setError(res.error); return; }
      setVersions(res.versions);
      setTicked(new Set(res.versions.map((v) => v.id)));
      setKeepId((res.versions.find((v) => v.keepsCode) || res.versions[0])?.id || null);
    });
    return () => { live = false; };
  }, [itemCode, loadCandidates]);

  const chosen = useMemo(() => (versions || []).filter((v) => ticked.has(v.id)), [versions, ticked]);
  const units = useMemo(() => [...new Set(chosen.map((v) => canonicalUnit(v.unit)))], [chosen]);
  const mixedUnits = units.length > 1;
  const keep = chosen.find((v) => v.id === keepId) || null;
  const others = chosen.filter((v) => v.id !== keepId);
  const moving = others.reduce((s, v) => s + v.rateCount, 0);
  const archiving = others.filter((v) => v.usageCount > 0).length;
  const deleting = others.length - archiving;
  const canCombine = keep && others.length > 0 && !mixedUnits && !submitting;

  const toggle = (id) => {
    const next = new Set(ticked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setTicked(next);
    // The kept version has to be one of the ticked ones.
    if (!next.has(keepId)) {
      const first = (versions || []).find((v) => next.has(v.id));
      setKeepId(first ? first.id : null);
    }
  };

  const pickKeep = (id) => {
    if (!ticked.has(id)) setTicked(new Set([...ticked, id]));
    setKeepId(id);
  };

  const handleCombine = async () => {
    setSubmitting(true);
    setError(null);
    const res = await onCombine(itemCode, chosen.map((v) => v.id), keepId);
    setSubmitting(false);
    if (!res.success) { setError(res.error); return; }
    onClose({ moved: res.moved, deleted: res.deleted, archived: res.archived });
  };

  let summary = '';
  if (versions && chosen.length < 2) summary = 'Tick at least two versions.';
  else if (keep && !mixedUnits) {
    const parts = [`${plural(moving, 'rate moves', 'rates move')} onto ${keep.fullCode}`];
    if (deleting) parts.push(`${plural(deleting, 'version', 'versions')} deleted`);
    if (archiving) parts.push(`${plural(archiving, 'version', 'versions')} archived (still used in a project or bill)`);
    summary = `${parts.join('; ')}.`;
  }

  const footer = (
    <>
      <span style={{ ...muted, marginRight: 'auto' }}>{summary}</span>
      <BrioButton variant="secondary" onClick={() => onClose()} disabled={submitting}>Cancel</BrioButton>
      <BrioButton variant="primary" onClick={handleCombine} disabled={!canCombine}>
        {submitting ? 'Combining…' : 'Combine'}
      </BrioButton>
    </>
  );

  return (
    <BrioModal title={`Combine versions of ${itemCode}`} onClose={() => onClose()} width="xl" footer={footer}>
      <BrioModalBody>
        <p style={{ fontSize: '0.82rem', margin: '0 0 0.6rem' }}>
          Tick the versions to merge and choose the one to keep. Its wording and unit stay, every rate on the other
          ticked versions moves onto it, and those versions are deleted. A version still used in a project BOQ,
          an FF&amp;E schedule or an issued bill is archived instead, so that document keeps its spec.
        </p>
        {mixedUnits && (
          <div style={{ color: '#a94442', fontSize: '0.82rem', marginBottom: '0.5rem' }}>
            The ticked versions are priced in different units ({units.join(', ')}), so their rates cannot sit on one
            spec. Untick the odd one out, or use Split instead.
          </div>
        )}
        {!versions && !error && <div style={muted}>Loading versions…</div>}
        {versions && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', ...muted }}>
                <th style={cell}>Merge</th>
                <th style={cell}>Keep</th>
                <th style={cell}>Version</th>
                <th style={cell}>Description</th>
                <th style={cell}>Unit</th>
                <th style={{ ...cell, textAlign: 'right' }}>Rates</th>
                <th style={cell}>After</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => {
                const on = ticked.has(v.id);
                const isKeep = on && v.id === keepId;
                return (
                  <tr key={v.id} style={{ opacity: on ? 1 : 0.55, background: isKeep ? 'rgba(159,144,42,0.12)' : undefined }}>
                    <td style={cell}>
                      <input type="checkbox" checked={on} onChange={() => toggle(v.id)} aria-label={`Merge ${v.fullCode}`} />
                    </td>
                    <td style={cell}>
                      <input type="radio" name="combine-keep" checked={isKeep} onChange={() => pickKeep(v.id)}
                        aria-label={`Keep ${v.fullCode}`} />
                    </td>
                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                      <code>{v.fullCode}</code>
                      {v.status !== 'active' && <div style={muted}>{v.status}</div>}
                    </td>
                    <td style={cell}>{v.shortDescription}</td>
                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>{canonicalUnit(v.unit)}</td>
                    <td style={{ ...cell, textAlign: 'right' }}>{v.rateCount}</td>
                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                      {!on ? <span style={muted}>stays as is</span>
                        : isKeep ? <span style={{ fontWeight: 600 }}>kept</span>
                        : v.usageCount > 0 ? <span title="Used in a project BOQ, FF&E schedule or issued bill">archived</span>
                        : <span style={{ color: '#a94442' }}>deleted</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {error && <div style={{ color: '#a94442', fontSize: '0.85rem', marginTop: '0.5rem' }}>{error}</div>}
      </BrioModalBody>
    </BrioModal>
  );
}

export default CombineVersionsModal;
